/** Canvas bootstrap failure classification + safe observability
 * (VISTOERA_HISTORICAL_CANVAS_LOAD_TIMEOUT_ROOT_CAUSE_AND_LARGE_CANVAS_RESILIENCE_P0_V1). */

import type { TFunction } from 'i18next'
import type { SaasApiError } from '../../../api'

export type CanvasLoadFailureCode =
  | 'NETWORK_TIMEOUT'
  | 'TRANSPORT_ERROR'
  | 'CANVAS_NOT_FOUND'
  | 'CANVAS_LOAD_FAILED'
  | 'CANVAS_SCHEMA_INVALID'
  | 'CANVAS_HYDRATION_FAILED'
  | 'ASSET_LOAD_FAILED'
  | 'SERVICE_UNAVAILABLE'
  | 'UNKNOWN'

export type CanvasLoadFailureStage = 'api' | 'hydration' | 'render' | 'asset'

export type CanvasLoadFailureContext = {
  workspaceId?: string
  projectId?: string
  canvasId?: string | null
  nodeCount?: number
  edgeCount?: number
  schemaVersion?: string | null
  loadStage?: CanvasLoadFailureStage
  durationMs?: number
}

export type ResolvedCanvasLoadFailure = {
  code: CanvasLoadFailureCode
  message: string
  detail?: string
}

const TIMEOUT_THRESHOLD_MS = 30_000

function isTimeoutTransport(err: SaasApiError): boolean {
  const msg = String(err.message || '')
  return err.code === 'ECONNABORTED' || err.code === 'NETWORK_TIMEOUT' || /timeout/i.test(msg)
}

export function classifyCanvasLoadFailure(err: unknown): CanvasLoadFailureCode {
  const e = err as SaasApiError
  const code = e?.code
  const status = e?.status

  if (isTimeoutTransport(e)) return 'NETWORK_TIMEOUT'
  if (code === 'TRANSPORT_ERROR') return 'TRANSPORT_ERROR'
  if (status === 404 || code === 'NOT_FOUND') return 'CANVAS_NOT_FOUND'
  if (code === 'CANVAS_SCHEMA_INVALID') return 'CANVAS_SCHEMA_INVALID'
  if (code === 'CANVAS_HYDRATION_FAILED') return 'CANVAS_HYDRATION_FAILED'
  if (code === 'ASSET_LOAD_FAILED') return 'ASSET_LOAD_FAILED'
  if (status === 422 || status === 409 || status === 400) return 'CANVAS_LOAD_FAILED'
  if (status != null && status >= 500) return 'SERVICE_UNAVAILABLE'
  return 'UNKNOWN'
}

export function resolveCanvasLoadFailureMessage(
  err: unknown,
  t: TFunction<'canvas'>,
): ResolvedCanvasLoadFailure {
  const e = err as SaasApiError
  const code = classifyCanvasLoadFailure(err)
  const rawDetail = typeof e?.message === 'string' && e.message.trim() ? e.message : undefined

  switch (code) {
    case 'NETWORK_TIMEOUT':
      return {
        code,
        message: t('page.loadErrors.networkTimeout', { seconds: TIMEOUT_THRESHOLD_MS / 1000 }),
        detail: rawDetail,
      }
    case 'TRANSPORT_ERROR':
      return { code, message: t('page.loadErrors.transportError'), detail: rawDetail }
    case 'CANVAS_NOT_FOUND':
      return { code, message: t('page.loadErrors.canvasNotFound'), detail: rawDetail }
    case 'CANVAS_SCHEMA_INVALID':
      return { code, message: t('page.loadErrors.schemaInvalid'), detail: rawDetail }
    case 'CANVAS_HYDRATION_FAILED':
      return { code, message: t('page.loadErrors.hydrationFailed'), detail: rawDetail }
    case 'ASSET_LOAD_FAILED':
      return { code, message: t('page.loadErrors.assetLoadFailed'), detail: rawDetail }
    case 'CANVAS_LOAD_FAILED':
      return {
        code,
        message: rawDetail || t('page.loadErrors.canvasLoadFailed'),
        detail: rawDetail,
      }
    case 'SERVICE_UNAVAILABLE':
      return { code, message: t('page.loadErrors.serviceUnavailable'), detail: rawDetail }
    default:
      return {
        code: 'UNKNOWN',
        message: rawDetail || t('page.unknownError'),
        detail: rawDetail,
      }
  }
}

export function logCanvasLoadFailure(
  err: unknown,
  context: CanvasLoadFailureContext,
): ResolvedCanvasLoadFailure {
  const resolved = classifyCanvasLoadFailure(err)
  const payload = {
    event: 'canvas_load_failed',
    canvas_id: context.canvasId ?? null,
    project_id: context.projectId ?? null,
    workspace_id: context.workspaceId ?? null,
    node_count: context.nodeCount ?? null,
    edge_count: context.edgeCount ?? null,
    schema_version: context.schemaVersion ?? null,
    load_stage: context.loadStage ?? 'api',
    duration_ms: context.durationMs ?? null,
    error_code: resolved,
    timeout_threshold_ms: TIMEOUT_THRESHOLD_MS,
  }
  console.error('[CanvasLoadFailure]', JSON.stringify(payload))
  if (typeof window !== 'undefined') {
    const w = window as unknown as { __CANVAS_LOAD_FAILURE__?: typeof payload }
    w.__CANVAS_LOAD_FAILURE__ = payload
  }
  return { code: resolved, message: '' }
}

export { TIMEOUT_THRESHOLD_MS }
